import type {
  DelegationView,
  ProfileView,
  QueryResult,
  RolesAccessView,
  SignInView,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';

/**
 * Identity, access and delegation, over HTTP.
 *
 * The whole identity port is served here now. The roles, profile and sign-in
 * screens were the last three reading fixtures, which mattered most for the
 * first of them: a permission matrix drawn from a file, on a platform whose
 * central claim is that authorisation is decided in the database, was the least
 * defensible screen in the product.
 *
 * The two write calls sit beside the reads rather than on the port, for the
 * same reason the governance repository's do: the port predates commands and
 * every method on it returns a read model.
 */

export interface DelegationDraft {
  toPersonId: string;
  roleCode: string;
  reason: string;
  validFrom: string;
  validTo: string;
}

export type WriteOutcome = { ok: true } | { ok: false; message: string };

/** Reads the server's own refusal, which names the rule that refused. */
async function refusal(response: Response): Promise<WriteOutcome> {
  const body = (await response.json().catch(() => ({}))) as { message?: string };
  return { ok: false, message: body.message ?? `Request failed (${response.status}).` };
}

export function createHttpIdentityRepository(baseUrl: string) {
  return {
    async getDelegation(): Promise<QueryResult<DelegationView>> {
      const response = await apiFetch(`${baseUrl}/governance/delegations`);
      if (!response.ok) throw new Error(`GET /governance/delegations failed: ${response.status}`);
      return (await response.json()) as QueryResult<DelegationView>;
    },

    async getRolesAccess(): Promise<QueryResult<RolesAccessView>> {
      const response = await apiFetch(`${baseUrl}/identity/roles`);
      if (!response.ok) throw new Error(`GET /identity/roles failed: ${response.status}`);
      return (await response.json()) as QueryResult<RolesAccessView>;
    },

    async getProfile(): Promise<QueryResult<ProfileView>> {
      const response = await apiFetch(`${baseUrl}/identity/profile`);
      if (!response.ok) throw new Error(`GET /identity/profile failed: ${response.status}`);
      return (await response.json()) as QueryResult<ProfileView>;
    },

    async getSignIn(): Promise<QueryResult<SignInView>> {
      const response = await apiFetch(`${baseUrl}/identity/sign-in`);
      if (!response.ok) throw new Error(`GET /identity/sign-in failed: ${response.status}`);
      return (await response.json()) as QueryResult<SignInView>;
    },

    async delegate(draft: DelegationDraft): Promise<WriteOutcome> {
      const response = await apiFetch(`${baseUrl}/governance/delegations`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      return response.ok ? { ok: true } : refusal(response);
    },

    async revoke(delegationId: string): Promise<WriteOutcome> {
      const response = await apiFetch(`${baseUrl}/governance/delegations/${delegationId}/revoke`, {
        method: 'POST',
      });
      return response.ok ? { ok: true } : refusal(response);
    },
  };
}
